import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Plus, 
  Filter, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  PencilRuler,
  Users,
  Link2,
  Calendar,
  MessageSquare,
  MoreVertical,
  Eye,
  Check,
  X,
  Trash2
} from 'lucide-react';
import { api, Concern, ConcernStatus, ConcernType, ConcernPriority, ConcernDetail, MonthlyPlan } from '../../lib/api';

interface ConcernsViewProps {
  projectId: number | string;
  monthlyPlans?: MonthlyPlan[];
  onRaiseConcern?: (taskIds?: number[]) => void;
}

const CONCERN_TYPES: { value: ConcernType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'drawing_missing', label: 'Drawing Missing', icon: <PencilRuler className="w-4 h-4" />, color: 'text-purple-600 bg-purple-50' },
  { value: 'resource_constraint', label: 'Resource Constraint', icon: <Users className="w-4 h-4" />, color: 'text-orange-600 bg-orange-50' },
  { value: 'dependency_issue', label: 'Dependency Issue', icon: <Link2 className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
  { value: 'schedule_risk', label: 'Schedule Risk', icon: <Calendar className="w-4 h-4" />, color: 'text-red-600 bg-red-50' },
  { value: 'other', label: 'Other', icon: <MessageSquare className="w-4 h-4" />, color: 'text-gray-600 bg-gray-50' }
];

const STATUS_CONFIG: Record<ConcernStatus, { label: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  open: { label: 'Open', icon: <AlertCircle className="w-4 h-4" />, color: 'text-red-600', bgColor: 'bg-red-50 border-red-200' },
  acknowledged: { label: 'Acknowledged', icon: <Eye className="w-4 h-4" />, color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200' },
  resolved: { label: 'Resolved', icon: <CheckCircle2 className="w-4 h-4" />, color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' },
  closed: { label: 'Closed', icon: <XCircle className="w-4 h-4" />, color: 'text-gray-500', bgColor: 'bg-gray-50 border-gray-200' }
};

const PRIORITY_CONFIG: Record<ConcernPriority, { label: string; color: string; dotColor: string }> = {
  low: { label: 'Low', color: 'text-gray-600', dotColor: 'bg-gray-400' },
  medium: { label: 'Medium', color: 'text-yellow-600', dotColor: 'bg-yellow-400' },
  high: { label: 'High', color: 'text-orange-600', dotColor: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'text-red-600', dotColor: 'bg-red-500' }
};

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const ConcernsView: React.FC<ConcernsViewProps> = ({ projectId, monthlyPlans = [], onRaiseConcern }) => {
  const [concerns, setConcerns] = useState<Concern[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConcern, setSelectedConcern] = useState<ConcernDetail | null>(null);
  const [expandedConcernId, setExpandedConcernId] = useState<number | null>(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState<ConcernStatus | ''>('');
  const [filterType, setFilterType] = useState<ConcernType | ''>('');
  const [filterPlanId, setFilterPlanId] = useState<number | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Action states
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);

  useEffect(() => {
    loadConcerns();
  }, [projectId, filterStatus, filterType, filterPlanId]);

  const loadConcerns = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterType) filters.type = filterType;
      if (filterPlanId) filters.monthly_plan_id = filterPlanId;
      
      const data = await api.getConcerns(projectId, filters);
      setConcerns(data);
    } catch (err) {
      console.error('Failed to load concerns:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConcernDetail = async (concernId: number) => {
    try {
      const detail = await api.getConcern(concernId);
      setSelectedConcern(detail);
    } catch (err) {
      console.error('Failed to load concern detail:', err);
    }
  };

  const handleAcknowledge = async (concernId: number) => {
    try {
      await api.acknowledgeConcern(concernId);
      loadConcerns();
      setActionMenuId(null);
    } catch (err) {
      alert('Failed to acknowledge concern');
    }
  };

  const handleResolve = async (concernId: number) => {
    try {
      await api.resolveConcern(concernId, resolutionNotes);
      setResolvingId(null);
      setResolutionNotes('');
      loadConcerns();
    } catch (err) {
      alert('Failed to resolve concern');
    }
  };

  const handleClose = async (concernId: number) => {
    try {
      await api.closeConcern(concernId);
      loadConcerns();
      setActionMenuId(null);
    } catch (err) {
      alert('Failed to close concern');
    }
  };

  const handleDelete = async (concernId: number) => {
    if (!confirm('Are you sure you want to delete this concern?')) return;
    try {
      await api.deleteConcern(concernId);
      loadConcerns();
      setActionMenuId(null);
    } catch (err) {
      alert('Failed to delete concern');
    }
  };

  const toggleExpand = async (concernId: number) => {
    if (expandedConcernId === concernId) {
      setExpandedConcernId(null);
      setSelectedConcern(null);
    } else {
      setExpandedConcernId(concernId);
      await loadConcernDetail(concernId);
    }
  };

  const getConcernTypeConfig = (type: ConcernType) => {
    return CONCERN_TYPES.find(t => t.value === type) || CONCERN_TYPES[4]; // default to 'other'
  };

  const stats = {
    total: concerns.length,
    open: concerns.filter(c => c.status === 'open').length,
    acknowledged: concerns.filter(c => c.status === 'acknowledged').length,
    resolved: concerns.filter(c => c.status === 'resolved').length,
    critical: concerns.filter(c => c.priority === 'critical' && c.status !== 'resolved' && c.status !== 'closed').length
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Concerns & Issues</h2>
              <p className="text-sm text-gray-500">Track and resolve project concerns</p>
            </div>
          </div>
          <button
            onClick={() => onRaiseConcern?.()}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Raise Concern
          </button>
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
            <span className="text-sm text-gray-500">Total:</span>
            <span className="text-sm font-semibold">{stats.total}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">{stats.open} Open</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 rounded-lg">
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-yellow-700">{stats.acknowledged} In Progress</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-700">{stats.resolved} Resolved</span>
          </div>
          {stats.critical > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-lg animate-pulse">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-700">{stats.critical} Critical</span>
            </div>
          )}
        </div>

        {/* Filters Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="mt-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <Filter className="w-4 h-4" />
          Filters
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ConcernStatus | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">All Status</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as ConcernType | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">All Types</option>
              {CONCERN_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            <select
              value={filterPlanId}
              onChange={(e) => setFilterPlanId(e.target.value ? parseInt(e.target.value) : '')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="">All Plans</option>
              {monthlyPlans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {MONTHS[plan.month]} {plan.year}
                </option>
              ))}
            </select>

            {(filterStatus || filterType || filterPlanId) && (
              <button
                onClick={() => {
                  setFilterStatus('');
                  setFilterType('');
                  setFilterPlanId('');
                }}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Concerns List */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : concerns.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Concerns Found</h3>
            <p className="text-gray-500 mb-4">
              {filterStatus || filterType || filterPlanId 
                ? 'Try adjusting your filters' 
                : 'Great! No concerns have been raised yet.'}
            </p>
            <button
              onClick={() => onRaiseConcern?.()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
            >
              <Plus className="w-4 h-4" />
              Raise First Concern
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {concerns.map(concern => {
              const typeConfig = getConcernTypeConfig(concern.concern_type);
              const statusConfig = STATUS_CONFIG[concern.status];
              const priorityConfig = PRIORITY_CONFIG[concern.priority];
              const isExpanded = expandedConcernId === concern.id;

              return (
                <div 
                  key={concern.id} 
                  className={`bg-white rounded-lg border ${statusConfig.bgColor} overflow-hidden transition-all duration-200`}
                >
                  {/* Concern Header */}
                  <div 
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50/50"
                    onClick={() => toggleExpand(concern.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <button className="mt-1 text-gray-400">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        
                        <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                          {typeConfig.icon}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color} ${statusConfig.bgColor}`}>
                              {statusConfig.icon}
                              {statusConfig.label}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${priorityConfig.color} bg-gray-100`}>
                              <span className={`w-2 h-2 rounded-full ${priorityConfig.dotColor}`}></span>
                              {priorityConfig.label}
                            </span>
                            <span className="text-xs text-gray-400 px-2 py-0.5 bg-gray-50 rounded">
                              {typeConfig.label}
                            </span>
                          </div>
                          
                          <h3 className="font-medium text-gray-900">{concern.title}</h3>
                          
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            {concern.plan_month && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {MONTHS[concern.plan_month]} {concern.plan_year}
                              </span>
                            )}
                            {concern.task_count !== undefined && concern.task_count > 0 && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {concern.task_count} task{concern.task_count > 1 ? 's' : ''}
                              </span>
                            )}
                            <span>
                              Created {new Date(concern.created_at).toLocaleDateString()}
                              {concern.created_by_name && ` by ${concern.created_by_name}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="relative" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setActionMenuId(actionMenuId === concern.id ? null : concern.id)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>

                        {actionMenuId === concern.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setActionMenuId(null)} />
                            <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                              {concern.status === 'open' && (
                                <button
                                  onClick={() => handleAcknowledge(concern.id)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Eye className="w-4 h-4 text-yellow-500" />
                                  Acknowledge
                                </button>
                              )}
                              {(concern.status === 'open' || concern.status === 'acknowledged') && (
                                <button
                                  onClick={() => {
                                    setResolvingId(concern.id);
                                    setActionMenuId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Check className="w-4 h-4 text-green-500" />
                                  Resolve
                                </button>
                              )}
                              {concern.status === 'resolved' && (
                                <button
                                  onClick={() => handleClose(concern.id)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <XCircle className="w-4 h-4 text-gray-500" />
                                  Close
                                </button>
                              )}
                              <hr className="my-1" />
                              <button
                                onClick={() => handleDelete(concern.id)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && selectedConcern && selectedConcern.id === concern.id && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      {/* Description */}
                      {selectedConcern.description && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">{selectedConcern.description}</p>
                        </div>
                      )}

                      {/* Resolution Notes */}
                      {selectedConcern.resolution_notes && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                          <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-1">
                            <CheckCircle2 className="w-4 h-4" />
                            Resolution
                          </div>
                          <p className="text-sm text-green-800">{selectedConcern.resolution_notes}</p>
                          {selectedConcern.resolved_at && (
                            <p className="text-xs text-green-600 mt-1">
                              Resolved on {new Date(selectedConcern.resolved_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Linked Tasks */}
                      {selectedConcern.tasks && selectedConcern.tasks.length > 0 && (
                        <div className="mt-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Linked Tasks ({selectedConcern.tasks.length})</h4>
                          <div className="space-y-1">
                            {selectedConcern.tasks.map(task => (
                              <div 
                                key={task.link_id} 
                                className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                              >
                                <div>
                                  <span className="text-gray-900">{task.activity_name}</span>
                                  <span className="text-gray-400 mx-2">•</span>
                                  <span className="text-gray-500">
                                    {[task.building_name, task.work_stage_name, task.category, task.floor, task.unit_name]
                                      .filter(Boolean).join(' → ')}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    task.progress >= 100 ? 'bg-green-100 text-green-700' :
                                    task.progress > 0 ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {task.progress}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Resolve Form */}
                      {resolvingId === concern.id && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">Resolve this concern</h4>
                          <textarea
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            placeholder="Add resolution notes (optional)..."
                            className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={3}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handleResolve(concern.id)}
                              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                            >
                              Mark as Resolved
                            </button>
                            <button
                              onClick={() => {
                                setResolvingId(null);
                                setResolutionNotes('');
                              }}
                              className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConcernsView;

